import { useEffect, useMemo, useState } from 'react';
import { useAppConfig } from '../../context/AppConfigContext';
import styles from './Statistics.module.css';
import { fetchData } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { handleError } from '../../context/errors/errorUtils';

const PERIODS = ['day', 'week', 'month'];

const Statistics = ({ isOpen, onClose }) => {
  const [tab, setTab] = useState('week');
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useAppConfig();
  const { userId } = useAuth();

  useEffect(() => {
    if (!isOpen || !userId) return;

    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const response = await fetchData('/api/user/statistics', 'POST', { userId });
        setStatsData(response || {});
      } catch (error) {
        handleError({
          code: 'ERROR_FETCHING_STATISTICS',
          error,
          context: { module: 'Statistics', message: 'Error fetching statistics' },
          showToUser: false,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [isOpen, userId]);

  const overview = statsData?.overview;
  const currentPeriod = statsData?.[tab];
  const topPlayers = currentPeriod?.topPlayers || [];
  const currentUserNumericId = Number(overview?.userNumericId) || null;
  const isCurrentUserInTop = topPlayers.some((player) => Number(player.id) === currentUserNumericId);
  const showCurrentUserRow = Boolean(currentPeriod?.userStats?.rank) && !isCurrentUserInTop;

  const maxChartValue = useMemo(
    () => Math.max(...(currentPeriod?.chartData || []).map((item) => item.phrasesCount || 0), 1),
    [currentPeriod]
  );

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2>{t.statistics?.title || 'Статистика'}</h2>
            <p className={styles.subtitle}>Фразы, слова и прогресс по блокам</p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className={styles.loading}>
            <p>{t.statistics?.loading || 'Загрузка...'}</p>
          </div>
        ) : (
          <div className={styles.statsContainer}>
            <section className={styles.overviewGrid}>
              <ProgressCard
                title="Фразы"
                value={overview?.totals?.phrases || 0}
                target={overview?.totals?.phrasesTarget || 35000}
                accent="orange"
              />
              <ProgressCard
                title="Слова"
                value={overview?.totals?.learnedWords || 0}
                target={overview?.totals?.learnedWordsTarget || 3000}
                accent="blue"
              />
              <BlocksCard
                completed={currentPeriod?.userStats?.blocksCompleted || 0}
                total={currentPeriod?.userStats?.blocksTotal || 0}
              />
            </section>

            <div className={styles.tabs}>
              {PERIODS.map((period) => (
                <button
                  key={period}
                  onClick={() => setTab(period)}
                  className={tab === period ? styles.active : ''}
                >
                  {t.statistics?.[period] || period}
                </button>
              ))}
            </div>

            <section className={styles.periodHeader}>
              <div>
                <h3>{getPeriodTitle(tab, t)}</h3>
                <p className={styles.periodSubtext}>Ваше место и результаты за выбранный период.</p>
              </div>
              <div className={styles.periodStats}>
                <div className={styles.periodBadge}>
                  <span>Место</span>
                  <strong>{currentPeriod?.userStats?.rank ? `#${currentPeriod.userStats.rank}` : '—'}</strong>
                </div>
                <div className={styles.periodBadge}>
                  <span>Фразы</span>
                  <strong>{(currentPeriod?.userStats?.phrasesCount || 0).toLocaleString()}</strong>
                </div>
                <div className={styles.periodBadge}>
                  <span>Слова</span>
                  <strong>{(currentPeriod?.userStats?.learnedWordsCount || 0).toLocaleString()}</strong>
                </div>
              </div>
            </section>

            <section className={styles.raceSection}>
              <h4>Лидеры периода</h4>
              {topPlayers.length || showCurrentUserRow ? (
                <div className={styles.tableWrap}>
                  <table className={styles.leaderboardTable}>
                    <thead>
                      <tr>
                        <th>Место</th>
                        <th>Фразы</th>
                        <th>Слова</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPlayers.map((player) => (
                        <tr key={`${tab}-${player.rank}-${player.id}`}>
                          <td>{player.rank}</td>
                          <td>{(player.phrasesCount || 0).toLocaleString()}</td>
                          <td>{(player.learnedWordsCount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                      {showCurrentUserRow && (
                        <tr className={styles.youRow}>
                          <td>{`Вы (${currentPeriod.userStats.rank})`}</td>
                          <td>{(currentPeriod?.userStats?.phrasesCount || 0).toLocaleString()}</td>
                          <td>{(currentPeriod?.userStats?.learnedWordsCount || 0).toLocaleString()}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={styles.emptyState}>{t.statistics?.no_leaderboard || 'Нет данных для рейтинга'}</p>
              )}
            </section>

            <section className={styles.chartSection}>
              <div className={styles.chartHeader}>
                <h4>Ритм практики</h4>
                <span>последние 10 периодов</span>
              </div>

              {currentPeriod?.chartData?.length ? (
                <div className={styles.chart}>
                  {currentPeriod.chartData.map((item) => {
                    const height = Math.max(8, ((item.phrasesCount || 0) / maxChartValue) * 100);
                    return (
                      <div key={`${tab}-${item.label}`} className={styles.chartColumn}>
                        <div className={styles.chartValue}>{item.phrasesCount}</div>
                        <div className={styles.chartBarWrap}>
                          <div className={styles.chartBar} style={{ height: `${height}%` }} />
                        </div>
                        <div className={styles.chartLabel}>{item.label}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={styles.emptyState}>{t.statistics?.no_chart_data || 'Нет данных для графика'}</p>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const ProgressCard = ({ title, value, target, accent }) => {
  const progress = Math.max(0, Math.min(100, Math.round((value / Math.max(target, 1)) * 100)));

  return (
    <div className={`${styles.card} ${styles[accent]}`}>
      <div className={styles.cardTitle}>{title}</div>
      <div className={styles.cardValue}>
        {value.toLocaleString()} <span>/ {target.toLocaleString()}</span>
      </div>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
      </div>
      <div className={styles.cardMeta}>{progress}% цели</div>
    </div>
  );
};

const BlocksCard = ({ completed, total }) => (
  <div className={`${styles.card} ${styles.neutral}`}>
    <div className={styles.cardTitle}>Проработано блоков</div>
    <div className={styles.cardValue}>
      {completed.toLocaleString()} <span>завершено</span>
    </div>
    <div className={styles.scoreNote}>Всего за период: {total.toLocaleString()}</div>
  </div>
);

const getPeriodTitle = (tab, t) => {
  if (tab === 'day') return t.statistics?.today || 'Сегодня';
  if (tab === 'week') return t.statistics?.this_week || 'Эта неделя';
  return t.statistics?.this_month || 'Этот месяц';
};

export default Statistics;
